import { SocketClient } from "cbcore-ts"
import { nil, NO, UIColor, UITextField, UITextView, UITooltip, UIView, YES } from "uicore-ts"
import { CBCheckbox } from "../Custom components/CB/CBCheckbox"
import {
    CBDataView,
    CBDataViewCellDescriptorWithObject,
    CBSelectionColumnDescriptor
} from "../Custom components/CB/CBDataView"
import { CBDialogViewShower } from "../Custom components/CB/CBDialogViewShower"
import { CBTableRowView } from "../Custom components/CB/CBTableRowView"
import { RHFocusManager } from "../Custom components/RH/Keyboard navigation/RHFocusManager"
import { RHBadgeView } from "../Custom components/RH/RHBadgeView"
import { RHButton } from "../Custom components/RH/RHButton"
import { RHCheckbox } from "../Custom components/RH/RHCheckbox"
import { RHColor } from "../Custom components/RH/RHColor"
import { RHDataView } from "../Custom components/RH/RHDataView"
import { RHTableActionButtonView } from "../Custom components/RH/RHTableActionButtonView"
import { RHTextArea } from "../Custom components/RH/RHTextArea"
import { RHTextField } from "../Custom components/RH/RHTextField"
import { RHCPVInviteCountCache } from "../Custom components/RH/Utils_slop/RHCPVInviteCountCache"
import { RHButtons, RHForms, RHSpacing } from "../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHEuroFormatter } from "../Custom components/RH/Utils_slop/RHEuroFormatter"
import { RHTextSanitizer } from "../Custom components/RH/Utils_slop/RHTextSanitizer"

import { RHInvitedCompaniesViewTokens } from "../Custom components/RH/Utils_slop/RHViewTokens"
import {
    CompanySearchResult,
    InvitedCompany,
    RHInvitedCompanySource,
    RHProcurementTemplateInvitationImportResult,
    RHProcurementTemplateInvitationImportRow,
    RHTaxDebt
} from "../SocketClientFunctions"
import { TaxDebtTooltipView } from "./InvitedCompaniesDialogView"
import { InvitedCompanyNameView } from "./InvitedCompanyNameView"
import { ProcurementInvitationState } from "./ProcurementInvitationState"


// Source badge configuration — shared with the dialog
export const INVITED_COMPANY_SOURCE_CONFIG: Record<
    RHInvitedCompanySource,
    { label: string; backgroundColor: UIColor; textColor: UIColor }
> = {
    "statistics": { label: "Statistika", backgroundColor: RHColor.primary, textColor: RHColor.textWhite },
    "company-interest": { label: "Ettevõtte CPV", backgroundColor: RHColor.success, textColor: RHColor.textWhite },
    "manually-added": { label: "Käsitsi", backgroundColor: RHColor.warning, textColor: RHColor.textBlack }
}

const INVITED_COMPANY_SOURCE_PRIORITY: RHInvitedCompanySource[] = [
    "statistics",
    "company-interest",
    "manually-added"
]

const SIMILAR_CONTRACTS_COLUMN_TITLE = "Lepinguid"
// old: "Sama CPV lepingute arv" — the automatic column sizing derives each column's
// minimum width from the single-line header text, so the long title forced this
// number column wider than the company column; the tooltip carries the full meaning
const SIMILAR_CONTRACTS_COLUMN_TOOLTIP =
    "Näitab, mitu sama CPV-koodiga lepingut on selle ettevõtjaga registri andmetel sõlmitud."

interface InvitedCompanyImportColumns {
    nameIndex?: number
    registryCodeIndex?: number
    emailIndex?: number
}

interface InvitedCompaniesForCPVSource {
    cpvCode: string
    companies: InvitedCompany[]
}

export interface RHInvitedCompanyPartDisplayInfo {
    partNumber: number
    cpvCode: string
}


class InvitedCompanySourceBadgesView extends UIView {

    _badgeViews: RHBadgeView[] = []


    updateWithSources(sources: RHInvitedCompanySource[]) {
        while (this._badgeViews.length > sources.length) {
            const badgeView = this._badgeViews.pop()
            badgeView?.removeFromSuperview()
        }

        sources.forEach((source, index) => {
            let badgeView = this._badgeViews[index]
            if (!badgeView) {
                badgeView = new RHBadgeView().addedAsSubviewToView(this)
                this._badgeViews[index] = badgeView
            }

            const sourceBadge = INVITED_COMPANY_SOURCE_CONFIG[source]
            badgeView.text = sourceBadge.label
            badgeView.setVariant(sourceBadge.backgroundColor, sourceBadge.textColor)
        })

        this.setNeedsLayout()
    }


    override layoutSubviews() {
        super.layoutSubviews()

        const bounds = this.contentBoundsWithInset(0)
        bounds
            .rectangleWithHeight(this.intrinsicContentHeight(bounds.width), 0.5)
            .framesByDistributingViewsAsColumn(
                this._badgeViews,
                RHSpacing.tiny,
                this._badgeViews
            )
    }


    override intrinsicContentHeight(constrainingWidth = 0) {
        let contentHeight = 0
        for (let index = 0; index < this._badgeViews.length; index++) {
            if (index > 0) {
                contentHeight += RHSpacing.tiny
            }
            contentHeight += this._badgeViews[index].intrinsicContentHeight(constrainingWidth)
        }
        return contentHeight
    }

}


/**
 * Standalone view that renders the invited-companies table plus the
 * manual-add form. Can be embedded inline (preview screen) or hosted
 * inside InvitedCompaniesDialogView.
 *
 * Ownership rules:
 *  - Call `loadForCPVCode(cpvCode)` to fetch and display companies for a
 *    single CPV code (editor flow).
 *  - Call `loadWithCompanies(companies)` to seed directly from a saved list
 *    (preview flow, where companies span multiple CPV codes).
 *  - Read `invitedCompanies` for the isInvited === true subset.
 *  - Read `allCompanies` for the full list (needed by SaveInvitedCompaniesForCPVCode).
 *  - Subscribe to `companiesDidChange` to react to any toggle or add/remove.
 *  - Subscribe to `manualCompaniesDidChange` when persisted manual rows
 *    need to be refreshed from the registry-enriched CPV source.
 */
export class InvitedCompaniesView extends UIView {

    // ─── Subviews ─────────────────────────────────────────────────────────────

    readonly companiesDataView: RHDataView<InvitedCompany> = new RHDataView<InvitedCompany>()
        .configuredWithObject({
            tableView: {
                allRowsHaveEqualHeight: NO
            },
            hasResizableColumns: NO,
            maximumIntrinsicContentHeight: RHInvitedCompaniesViewTokens.companiesTableHeight
        })
        .addedAsSubviewToView(this)

    readonly sourceControlsLabel = new UITextView()
        .configuredWithObject({
            text: "Allikad",
            style: {
                fontWeight: "600",
                fontSize: "0.9em",
                color: RHColor.textSecondary.stringValue
            }
        })
        .addedAsSubviewToView(this)

    readonly statisticsSourceCheckbox = new RHCheckbox()
        .configuredWithObject({
            titleLabel: { text: "Statistika" },
            selected: YES,
            controlEventTargetAccumulator: {
                SelectionChange: () => {
                    this._sourcePolicyDidChange("statistics", this.statisticsSourceCheckbox.selected)
                }
            }
        })
        .addedAsSubviewToView(this)

    readonly companyInterestSourceCheckbox = new RHCheckbox()
        .configuredWithObject({
            titleLabel: { text: "Ettevõtte CPV-huvid" },
            selected: YES,
            controlEventTargetAccumulator: {
                SelectionChange: () => {
                    this._sourcePolicyDidChange("company-interest", this.companyInterestSourceCheckbox.selected)
                }
            }
        })
        .addedAsSubviewToView(this)

    readonly manuallyAddedSourceCheckbox = new RHCheckbox()
        .configuredWithObject({
            titleLabel: { text: "Käsitsi/import" },
            selected: YES,
            controlEventTargetAccumulator: {
                SelectionChange: () => {
                    this._sourcePolicyDidChange("manually-added", this.manuallyAddedSourceCheckbox.selected)
                }
            }
        })
        .addedAsSubviewToView(this)

    readonly addSectionLabel = new UITextView()
        .configuredWithObject({
            text: "Lisa ettevõte käsitsi",
            style: {
                fontWeight: "600",
                fontSize: "0.9em",
                color: RHColor.textSecondary.stringValue
            }
        })
        .addedAsSubviewToView(this)

    readonly addNameTextField = new RHTextField()
        .configuredWithObject({
            placeholderText: "Ettevõtte nimi",
            isRequired: YES,
            validationRegex: /\p{L}/u,
            validateOnInput: YES
        })
        .addedAsSubviewToView(this)

    readonly addRegcodeTextField = new RHTextField()
        .configuredWithObject({
            placeholderText: "Registrikood",
            isRequired: YES,
            validationRegex: /^\d{8}$/,
            validateOnInput: YES
        })
        .addedAsSubviewToView(this)

    readonly addEmailTextField = new RHTextField()
        .configuredWithObject({
            placeholderText: "E-posti aadress",
            type: "email",
            isRequired: YES,
            validationRegex: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            validateOnInput: YES
        })
        .addedAsSubviewToView(this)

    readonly addCompanyButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "+ Lisa" },
            controlEventTargetAccumulator: {
                PrimaryActionTriggered: () => {
                    this._addCompanyManually()
                }
            }
        })
        .addedAsSubviewToView(this)

    readonly importSectionLabel = new UITextView()
        .configuredWithObject({
            text: "Impordi nimekiri",
            style: {
                fontWeight: "600",
                fontSize: "0.9em",
                color: RHColor.textSecondary.stringValue
            }
        })
        .addedAsSubviewToView(this)

    readonly removeAllManuallyAddedCompaniesButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Eemalda kõik käsitsi lisatud" },
            variant: RHButtons.destructiveUtilityVariant,
            contentPadding: RHButtons.compactContentPadding,
            hidden: YES,
            style: {
                fontSize: RHButtons.compactFontSize
            },
            controlEventTargetAccumulator: {
                PrimaryActionTriggered: () => {
                    this._requestRemovalOfAllManuallyAddedCompanies()
                }
            }
        })
        .addedAsSubviewToView(this)

    readonly importTextArea = new RHTextArea()
        .configuredWithObject({
            placeholderText: "Kleebi CSV read: ettevõtte nimi; registrikood; e-post"
        })
        .addedAsSubviewToView(this)

    readonly importCompaniesButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Impordi" },
            variant: RHInvitedCompaniesViewTokens.manualImportButtonVariant,
            controlEventTargetAccumulator: {
                PrimaryActionTriggered: () => {
                    void this._importCompaniesFromText()
                }
            }
        })
        .addedAsSubviewToView(this)

    // ─── Focus management ─────────────────────────────────────────────────────

    override _focusManager = new RHFocusManager(() => {
        const items: UIView[] = []
        if (this._showsManagementControls) {
            items.push(
                this.statisticsSourceCheckbox,
                this.companyInterestSourceCheckbox,
                this.manuallyAddedSourceCheckbox
            )
            if (!this.removeAllManuallyAddedCompaniesButton.hidden) {
                items.push(this.removeAllManuallyAddedCompaniesButton)
            }
        }
        items.push(this.companiesDataView.searchTextField)
        if (this._showsManagementControls) {
            items.push(
                this.addNameTextField,
                this.addRegcodeTextField,
                this.addEmailTextField,
                this.addCompanyButton
            )
            items.push(
                this.importTextArea,
                this.importCompaniesButton
            )
        }
        return items
    })

    // ─── State ────────────────────────────────────────────────────────────────

    /**
     * Unique key used to namespace this view's selection set in the static
     * CBDataView selection store. Must be unique per instance so that multiple
     * InvitedCompaniesView instances (one per part) do not share — and
     * inadvertently clear — each other's selection state.
     */
    private readonly _selectionStoreKey = `invitedCompanies_${Math.random().toString(36).slice(2)}`

    _companies: InvitedCompany[] = []
    _sourcePolicyEnabled: Record<RHInvitedCompanySource, boolean> = {
        "statistics": YES,
        "company-interest": YES,
        "manually-added": YES
    }
    _explicitSelectionOverrideKeys = new Set<string>()
    _lastInvitationStateByKey = new Map<string, boolean>()
    _isApplyingSourcePolicy = NO
    _showsManagementControls = YES
    inviteCountCache = new RHCPVInviteCountCache()
    _companiesBySourceCPVCode = new Map<string, InvitedCompany[]>()
    _locallyAddedCompanyKeys = new Set<string>()
    _sourceLoadGeneration = 0

    /**
     * Fired after any isInvited toggle, manual add, or manual removal.
     * The dialog and the preview controller subscribe to this.
     */
    companiesDidChange?: () => void
    manualCompaniesDidChange?: () => void
    selectionSnapshotIsAuthoritative = NO
    templateImportResolver?: (
        rows: RHProcurementTemplateInvitationImportRow[]
    ) => Promise<RHProcurementTemplateInvitationImportResult>

    // Company search autocomplete state
    private _searchDebounceTimer?: ReturnType<typeof setTimeout>
    _companySearchGeneration = 0
    private _lastSearchResults: CompanySearchResult[] = []

    // Tooltip tracking per cell view
    private _tooltipMap = new WeakMap<object, TaxDebtTooltipView>()
    private _debtMap = new WeakMap<object, { debt: RHTaxDebt; badgeColor: RHColor }>()

    // ─── Public API ───────────────────────────────────────────────────────────

    /**
     * The subset of companies the procurer has marked as invited.
     */
    get invitedCompanies(): InvitedCompany[] {
        return this._companies.filter(company => company.isInvited)
    }

    /**
     * The full list including un-invited entries. Needed by
     * SaveInvitedCompaniesForCPVCode so exclusions are persisted.
     */
    get allCompanies(): InvitedCompany[] {
        return [...this._companies]
    }


    companiesForSourceCustomization(cpvCode: string): InvitedCompany[] {
        return ProcurementInvitationState.companiesForSourceCustomization(
            this._companies,
            cpvCode,
            this._companiesBySourceCPVCode,
            this._locallyAddedCompanyKeys
        )
    }


    /**
     * Count of companies currently marked as invited.
     */
    get selectedCount(): number {
        return this._companies.filter(company => company.isInvited).length
    }


    _normalizedInvitedCompany(company: InvitedCompany | (Partial<InvitedCompany> & { source?: string })): InvitedCompany {
        const sources = this._normalizedSourcesForCompany(company)
        const { source: _legacySource, ...companyWithoutLegacySource } = company as Partial<InvitedCompany> & { source?: string }
        return {
            ...companyWithoutLegacySource,
            isInvited: company.isInvited ? YES : NO,
            sources
        } as InvitedCompany
    }


    _normalizedSourcesForCompany(company: Partial<InvitedCompany> & { source?: string }): RHInvitedCompanySource[] {
        const sources = new Set<RHInvitedCompanySource>()

        if (Array.isArray(company.sources)) {
            for (const source of company.sources) {
                const normalizedSource = this._normalizedSource(source)
                if (normalizedSource) {
                    sources.add(normalizedSource)
                }
            }
        }

        const legacySource = this._normalizedSource(company.source)
        if (legacySource) {
            sources.add(legacySource)
        }

        if (sources.size === 0) {
            sources.add("manually-added")
        }

        return INVITED_COMPANY_SOURCE_PRIORITY.filter(source => sources.has(source))
    }


    _normalizedSource(source: string | undefined): RHInvitedCompanySource | undefined {
        if (source === "statistics") {
            return "statistics"
        }
        if (source === "company-interest" || source === "self-registered") {
            return "company-interest"
        }
        if (source === "manually-added") {
            return "manually-added"
        }
        return undefined
    }


    _identityKeyForCompany(company: Pick<InvitedCompany, "name" | "registryCode" | "email" | "_id">): string {
        return ProcurementInvitationState.identityKeyForCompany(company)
    }


    _companyIsEligibleBySourcePolicy(company: InvitedCompany): boolean {
        for (const source of company.sources) {
            if (this._sourcePolicyEnabled[source]) {
                return YES
            }
        }
        return NO
    }


    _companyIsSelectable(company: InvitedCompany): boolean {
        if (this._explicitSelectionOverrideKeys.has(this._identityKeyForCompany(company))) {
            return YES
        }
        return this._companyIsEligibleBySourcePolicy(company)
    }


    /**
     * Rows with no source currently enabled (and no explicit per-row override)
     * are dropped from the table entirely rather than shown disabled — the
     * dialog should only ever list companies that would actually be invited.
     */
    _selectableCompanies(): InvitedCompany[] {
        return this._companies.filter(company => this._companyIsSelectable(company))
    }


    _defaultInvitationForCompany(_company: InvitedCompany): boolean {
        return NO
    }


    _resetSelectionOverridesForLoadedCompanies() {
        this._explicitSelectionOverrideKeys.clear()
        for (const company of this._companies) {
            const defaultInvited = this._defaultInvitationForCompany(company)
            if ((company.isInvited && !defaultInvited) || (!company.isInvited && defaultInvited)) {
                this._explicitSelectionOverrideKeys.add(this._identityKeyForCompany(company))
            }
        }
        this._refreshLastInvitationStates()
    }


    _refreshLastInvitationStates() {
        this._lastInvitationStateByKey = new Map<string, boolean>()
        for (const company of this._companies) {
            this._lastInvitationStateByKey.set(this._identityKeyForCompany(company), company.isInvited)
        }
    }


    _recordSelectionOverridesFromCurrentSelection() {
        for (const company of this._companies) {
            const identityKey = this._identityKeyForCompany(company)
            const previousValue = this._lastInvitationStateByKey.get(identityKey)
            if (previousValue !== undefined && previousValue !== company.isInvited) {
                this._explicitSelectionOverrideKeys.add(identityKey)
            }
        }
        this._refreshLastInvitationStates()
    }


    _sourcePolicyDidChange(source: RHInvitedCompanySource, enabled: boolean) {
        this._sourcePolicyEnabled[source] = enabled
        this._applySourcePolicyToCompanies()
    }


    _resetSourcePolicyControls() {
        this._sourcePolicyEnabled = {
            "statistics": YES,
            "company-interest": YES,
            "manually-added": YES
        }
        this.statisticsSourceCheckbox.selected = YES
        this.companyInterestSourceCheckbox.selected = YES
        this.manuallyAddedSourceCheckbox.selected = YES
    }


    _enableManuallyAddedSourcePolicy() {
        this._sourcePolicyEnabled["manually-added"] = YES
        this.manuallyAddedSourceCheckbox.selected = YES
    }


    _applySourcePolicyToCompanies() {
        this._isApplyingSourcePolicy = YES
        for (const company of this._companies) {
            if (this._explicitSelectionOverrideKeys.has(this._identityKeyForCompany(company))) {
                continue
            }
            if (!this._companyIsEligibleBySourcePolicy(company)) {
                company.isInvited = NO
                continue
            }
            company.isInvited = this._defaultInvitationForCompany(company)
        }
        this._seedSelectionStore(this._companies)
        this.companiesDataView.data = this._selectableCompanies()
        this._refreshLastInvitationStates()
        this._isApplyingSourcePolicy = NO
        this.companiesDidChange?.()
    }

    /**
     * Fetches the company list for a single CPV code from the server and
     * displays it. Used by the editor flow (per-part dialog).
     */
    async loadForCPVCode(cpvCode: string) {
        return this.loadForCPVCodes([cpvCode])
    }


    async loadForCPVCodes(
        cpvCodes: string[],
        selectionSnapshotIsAuthoritative = NO,
        availableFinancialsAndTrackRecord: InvitedCompany[] = []
    ) {
        const sourceLoadGeneration = ++this._sourceLoadGeneration
        try {
            const sourceLists: InvitedCompaniesForCPVSource[] = []
            for (const cpvCode of cpvCodes) {
                const companies = await this.inviteCountCache.retrieveCompaniesForCPVCode(cpvCode)
                sourceLists.push({
                    cpvCode,
                    companies
                })
            }
            if (sourceLoadGeneration !== this._sourceLoadGeneration) {
                return NO
            }
            this._loadAndDisplayCompaniesFromCPVSources(
                sourceLists,
                selectionSnapshotIsAuthoritative,
                availableFinancialsAndTrackRecord
            )
            return YES
        }
        catch (error) {
            if (sourceLoadGeneration !== this._sourceLoadGeneration) {
                return NO
            }
            console.error("Error loading invited companies:", error)
            CBDialogViewShower.alert("Ettevõtete laadimine ebaõnnestus")
            return NO
        }
    }


    invalidatePendingSourceLoads() {
        this._sourceLoadGeneration++
    }

    /**
     * Clears the displayed rows so a host (the dialog) can paint its loading
     * spinner before the next, potentially expensive, variable-height row
     * measurement runs. The in-memory `_companies` list stays untouched and is
     * the source of truth; `loadForCPVCode` repopulates the table afterwards.
     */
    async showLoadingPlaceholder() {
        this.companiesDataView.data = []
        // The data setter filters asynchronously; await it so the table model is
        // actually empty before the caller forces a synchronous layout pass.
        await this.companiesDataView.updateTableDataByFiltering()
    }

    /**
     * Seeds the view directly from a saved list without a server fetch.
     * Used by the preview flow where the list was already persisted on the
     * procurement document. Resolves after the data view has applied its
     * asynchronous filtering, so callers may safely reveal measured content.
     */
    async loadWithCompanies(
        companies: InvitedCompany[],
        selectionSnapshotIsAuthoritative = NO
    ) {
        this.invalidatePendingSourceLoads()
        this._companiesBySourceCPVCode.clear()
        this._locallyAddedCompanyKeys.clear()
        this.selectionSnapshotIsAuthoritative = selectionSnapshotIsAuthoritative
        this._loadAndDisplay(companies)
        await this.companiesDataView.updateTableDataByFiltering()
    }

    /**
     * Populates the in-memory list from a server response only when the list
     * is still empty — in-memory state always wins. Called by
     * ProcurementPartEditorView._loadCompanyCountsForCPVCode to prime the counts
     * badge before the dialog is first opened.
     */
    seedIfEmpty(companies: InvitedCompany[], sourceCPVCode?: string) {
        if (this.selectionSnapshotIsAuthoritative || this._companies.length > 0) {
            return
        }
        this._resetSourcePolicyControls()
        this._companies = companies.map(company => this._normalizedInvitedCompany(company))
        this._companiesBySourceCPVCode.clear()
        if (sourceCPVCode) {
            this._companiesBySourceCPVCode.set(sourceCPVCode, [...this._companies])
        }
        this._resetSelectionOverridesForLoadedCompanies()
        this._seedSelectionStore(this._companies)
        this.companiesDataView.data = this._selectableCompanies()
        this._updateRemoveAllManuallyAddedCompaniesButtonState()
    }

    // ─── Internal helpers ─────────────────────────────────────────────────────

    private _loadAndDisplay(companies: InvitedCompany[]) {
        this._resetSourcePolicyControls()
        this._companies = companies.map(company => this._normalizedInvitedCompany(company))
        this._resetSelectionOverridesForLoadedCompanies()
        this._seedSelectionStore(this._companies)
        this.companiesDataView.data = this._selectableCompanies()
        this._updateRemoveAllManuallyAddedCompaniesButtonState()
        this.companiesDidChange?.()
    }


    _loadAndDisplayCompaniesFromCPVSources(
        sourceLists: InvitedCompaniesForCPVSource[],
        selectionSnapshotIsAuthoritative = NO,
        availableFinancialsAndTrackRecord: InvitedCompany[] = []
    ) {
        const previousInvitationStateByKey = new Map<string, boolean>()
        const previousCompanyKeys = new Set<string>()
        for (const company of this._companies) {
            const identityKey = this._identityKeyForCompany(company)
            previousCompanyKeys.add(identityKey)
            previousInvitationStateByKey.set(identityKey, company.isInvited)
        }
        const previousOverrideKeys = new Set(this._explicitSelectionOverrideKeys)
        const manualCompanies = this._manualCompaniesForReload()
        const invitedCompanies =
            ProcurementInvitationState.invitedCompaniesToRetainDuringSourceReload(
                this._companies
            )

        const mergedCompanies: InvitedCompany[] = []
        for (const sourceList of sourceLists) {
            for (const company of sourceList.companies) {
                const normalizedCompany = this._normalizedInvitedCompany(company)
                if (
                    selectionSnapshotIsAuthoritative &&
                    !previousCompanyKeys.has(this._identityKeyForCompany(normalizedCompany))
                ) {
                    normalizedCompany.isInvited = NO
                }
                this._mergeCompanyIntoArray(
                    normalizedCompany,
                    mergedCompanies,
                    previousOverrideKeys,
                    previousInvitationStateByKey
                )
            }
        }

        for (const manualCompany of manualCompanies) {
            this._mergeManualCompanyIntoArray(manualCompany, mergedCompanies)
        }
        for (const invitedCompany of invitedCompanies) {
            this._mergeInvitedCompanyIntoArray(invitedCompany, mergedCompanies)
        }

        this._companiesBySourceCPVCode = new Map(
            sourceLists.map(sourceList => [
                sourceList.cpvCode,
                sourceList.companies.map(company => this._normalizedInvitedCompany(company))
            ] as [string, InvitedCompany[]])
        )
        this.selectionSnapshotIsAuthoritative = selectionSnapshotIsAuthoritative
        this._companies = ProcurementInvitationState.companiesByAddingAvailableFinancialsAndTrackRecord(
            mergedCompanies,
            availableFinancialsAndTrackRecord
        )
        this._resetSelectionOverridesForLoadedCompanies()
        this._seedSelectionStore(this._companies)
        this.companiesDataView.data = this._selectableCompanies()
        this._updateRemoveAllManuallyAddedCompaniesButtonState()
        this.companiesDidChange?.()
    }


    _manualCompaniesForReload(): InvitedCompany[] {
        return this._companies
            .filter(company => company.sources.includes("manually-added"))
            .map(company => ({
                ...company,
                sources: ["manually-added"]
            }))
    }


    _mergeCompanyIntoArray(
        incomingCompany: InvitedCompany,
        companies: InvitedCompany[],
        previousOverrideKeys: Set<string>,
        previousInvitationStateByKey: Map<string, boolean>
    ) {
        const incomingKey = this._identityKeyForCompany(incomingCompany)
        const existingCompany = companies.find(company =>
            this._identityKeyForCompany(company) === incomingKey
        )

        if (existingCompany) {
            existingCompany.sources = this._sourcesByAddingSourcesToCompany(
                existingCompany,
                incomingCompany.sources
            )
            this._copyMissingCompanyFieldsFromCompany(existingCompany, incomingCompany)
            if (!previousOverrideKeys.has(incomingKey) && incomingCompany.isInvited) {
                existingCompany.isInvited = YES
            }
            return
        }

        const companyToAdd = { ...incomingCompany }
        if (previousOverrideKeys.has(incomingKey)) {
            const previousInvitationState = previousInvitationStateByKey.get(incomingKey)
            if (previousInvitationState !== undefined) {
                companyToAdd.isInvited = previousInvitationState
            }
        }
        companies.push(companyToAdd)
    }


    _mergeManualCompanyIntoArray(manualCompany: InvitedCompany, companies: InvitedCompany[]) {
        const manualKey = this._identityKeyForCompany(manualCompany)
        const existingCompany = companies.find(company =>
            this._identityKeyForCompany(company) === manualKey
        )

        if (existingCompany) {
            existingCompany.sources = this._sourcesByAddingSourceToCompany(existingCompany, "manually-added")
            this._copyMissingCompanyFieldsFromCompany(existingCompany, manualCompany)
            if (manualCompany.email) {
                existingCompany.email = manualCompany.email
            }
            existingCompany.isInvited = manualCompany.isInvited
            return
        }

        companies.push({
            ...manualCompany,
            sources: ["manually-added"]
        })
    }


    _mergeInvitedCompanyIntoArray(
        invitedCompany: InvitedCompany,
        companies: InvitedCompany[]
    ) {
        const invitedKey = this._identityKeyForCompany(invitedCompany)
        const existingCompany = companies.find(company =>
            this._identityKeyForCompany(company) === invitedKey
        )

        if (existingCompany) {
            existingCompany.sources = this._sourcesByAddingSourcesToCompany(
                existingCompany,
                invitedCompany.sources
            )
            this._copyMissingCompanyFieldsFromCompany(existingCompany, invitedCompany)
            existingCompany.isInvited = YES
            return
        }

        companies.push({
            ...invitedCompany,
            isInvited: YES,
            sources: [...invitedCompany.sources]
        })
    }


    _copyMissingCompanyFieldsFromCompany(targetCompany: InvitedCompany, sourceCompany: InvitedCompany) {
        if (!targetCompany._id && sourceCompany._id) {
            targetCompany._id = sourceCompany._id
        }
        if (!targetCompany.email && sourceCompany.email) {
            targetCompany.email = sourceCompany.email
        }
        if (!targetCompany.registryCode && sourceCompany.registryCode) {
            targetCompany.registryCode = sourceCompany.registryCode
        }
        if (targetCompany.taxDebt === undefined && sourceCompany.taxDebt !== undefined) {
            targetCompany.taxDebt = sourceCompany.taxDebt
        }
        if (targetCompany.similarContractsCount === undefined && sourceCompany.similarContractsCount !== undefined) {
            targetCompany.similarContractsCount = sourceCompany.similarContractsCount
        }
        if (targetCompany.taxableTurnoverThousands === undefined && sourceCompany.taxableTurnoverThousands !== undefined) {
            targetCompany.taxableTurnoverThousands = sourceCompany.taxableTurnoverThousands
        }
        if (targetCompany.employeesCount === undefined && sourceCompany.employeesCount !== undefined) {
            targetCompany.employeesCount = sourceCompany.employeesCount
        }
    }

    private _seedSelectionStore(companies: InvitedCompany[]) {
        let store = CBDataView._selectionStore.get(this._selectionStoreKey)
        if (!store) {
            store = new Set()
            CBDataView._selectionStore.set(this._selectionStoreKey, store)
        }
        else {
            store.clear()
        }
        for (const company of companies) {
            if (company.isInvited) {
                store.add(CBDataView._tokenForItem(company as object))
            }
        }
    }

    async _addCompanyManually() {
        const name = this.addNameTextField.text.trim()
        const registryCode = this.addRegcodeTextField.text.trim()
        const email = this.addEmailTextField.text.trim()

        this.addNameTextField.text = name
        this.addRegcodeTextField.text = registryCode
        this.addEmailTextField.text = email

        if (!this.addNameTextField.validate()) {
            CBDialogViewShower.alert("Sisesta korrektne ettevõtte nimi", () => {
                this.addNameTextField.focus()
            })
            return
        }

        if (!this.addRegcodeTextField.validate()) {
            CBDialogViewShower.alert("Registrikood peab koosnema 8 numbrist", () => {
                this.addRegcodeTextField.focus()
            })
            return
        }

        if (!this.addEmailTextField.validate()) {
            CBDialogViewShower.alert("Sisesta korrektne e-posti aadress", () => {
                this.addEmailTextField.focus()
            })
            return
        }

        const company: InvitedCompany = {
            name,
            email,
            registryCode: registryCode || undefined,
            isInvited: YES,
            sources: ["manually-added"]
        }

        this._mergeImportedCompanies([company])
        this.companiesDataView.searchTextField.text = ""

        this.addNameTextField.text = ""
        this.addRegcodeTextField.text = ""
        this.addEmailTextField.text = ""
        this.addNameTextField.resetValidationVisuals()
        this.addRegcodeTextField.resetValidationVisuals()
        this.addEmailTextField.resetValidationVisuals()

        this.addNameTextField.nativeAutocompleteData = []
        this.addRegcodeTextField.nativeAutocompleteData = []
        this._lastSearchResults = []

        this._seedSelectionStore(this._companies)
        this.companiesDataView.data = this._selectableCompanies()
        this._updateRemoveAllManuallyAddedCompaniesButtonState()
        this._refreshLastInvitationStates()
        this.companiesDidChange?.()
        this.manualCompaniesDidChange?.()
    }


    async _importCompaniesFromText() {
        if (this.templateImportResolver) {
            const rows = this._templateImportRowsFromText(this.importTextArea.text)
            if (rows.length === 0) {
                CBDialogViewShower.alert("Imporditavaid ettevõtteid ei leitud.")
                return
            }
            this.importCompaniesButton.enabled = NO
            try {
                const resolution = await this.templateImportResolver(rows)
                const importedCompanies: InvitedCompany[] = resolution.accepted.map(company => ({
                    name: company.name,
                    registryCode: company.registryCode,
                    email: company.email,
                    isInvited: YES,
                    sources: ["manually-added"]
                }))
                const changedCount = this._mergeImportedCompanies(importedCompanies)
                if (changedCount > 0) {
                    this.companiesDataView.searchTextField.text = ""
                }
                this.importTextArea.text = ""
                this._seedSelectionStore(this._companies)
                this.companiesDataView.data = this._selectableCompanies()
                this._updateRemoveAllManuallyAddedCompaniesButtonState()
                this._refreshLastInvitationStates()
                this.companiesDidChange?.()
                this.manualCompaniesDidChange?.()
                const skippedSuffix = resolution.skipped.length > 0
                    ? ` ${resolution.skipped.length} rida jäeti vahele.`
                    : ""
                CBDialogViewShower.alert(`Imporditud ${changedCount} ettevõtet.${skippedSuffix}`)
            }
            catch (error) {
                console.error("InvitedCompaniesView: template import resolution failed", error)
                CBDialogViewShower.alert("Ettevõtete import ebaõnnestus.")
            }
            finally {
                this.importCompaniesButton.enabled = YES
            }
            return
        }

        const importedCompanies = this._importedCompaniesFromText(this.importTextArea.text)
        if (importedCompanies.length === 0) {
            CBDialogViewShower.alert("Imporditavaid ettevõtteid ei leitud.")
            return
        }

        const changedCount = this._mergeImportedCompanies(importedCompanies)
        if (changedCount > 0) {
            this.companiesDataView.searchTextField.text = ""
        }
        this.importTextArea.text = ""
        this._seedSelectionStore(this._companies)
        this.companiesDataView.data = this._selectableCompanies()
        this._updateRemoveAllManuallyAddedCompaniesButtonState()
        this._refreshLastInvitationStates()
        this.companiesDidChange?.()
        this.manualCompaniesDidChange?.()
        CBDialogViewShower.alert(`Imporditud ${changedCount} ettevõtet.`)
    }


    _templateImportRowsFromText(importText: string): RHProcurementTemplateInvitationImportRow[] {
        const lines = importText
            .split(/\r?\n/)
            .map(line => line.trim())
            .filter(line => line.length > 0)
        let columns: InvitedCompanyImportColumns | undefined
        const rows: RHProcurementTemplateInvitationImportRow[] = []
        lines.forEach((line, index) => {
            const cells = this._cellsFromImportedLine(line)
            if (!columns && this._cellsLookLikeImportHeader(cells)) {
                columns = this._columnsFromImportHeader(cells)
                return
            }
            const name = this._cellValueAtIndex(cells, columns?.nameIndex ?? 0)
            const registryCode = this._cellValueAtIndex(cells, columns?.registryCodeIndex ?? 1)
            const email = this._cellValueAtIndex(cells, columns?.emailIndex ?? 2)
            rows.push({
                rowNumber: index + 1,
                name: name || undefined,
                registryCode: registryCode || undefined,
                email: email || undefined
            })
        })
        return rows
    }


    _importedCompaniesFromText(importText: string): InvitedCompany[] {
        const importedCompanies: InvitedCompany[] = []
        const lines = importText
            .split(/\r?\n/)
            .map(line => line.trim())
            .filter(line => line.length > 0)

        let columns: InvitedCompanyImportColumns | undefined
        for (const line of lines) {
            const cells = this._cellsFromImportedLine(line)
            if (!columns && this._cellsLookLikeImportHeader(cells)) {
                columns = this._columnsFromImportHeader(cells)
                continue
            }

            const company = this._importedCompanyFromCells(cells, columns)
            if (company) {
                importedCompanies.push(company)
            }
        }

        return importedCompanies
    }


    _cellsFromImportedLine(line: string): string[] {
        const separator = this._separatorForImportedLine(line)
        const cells: string[] = []
        let currentCell = ""
        let isInsideQuotes = NO

        for (const character of line) {
            if (character === "\"") {
                isInsideQuotes = !isInsideQuotes
                continue
            }
            if (character === separator && !isInsideQuotes) {
                cells.push(currentCell.trim())
                currentCell = ""
                continue
            }
            currentCell += character
        }

        cells.push(currentCell.trim())
        return cells
    }


    _separatorForImportedLine(line: string): string {
        if (line.includes(";")) {
            return ";"
        }
        if (line.includes("\t")) {
            return "\t"
        }
        return ","
    }


    _cellsLookLikeImportHeader(cells: string[]): boolean {
        const headerText = cells.join(" ").toLowerCase()
        return (
            headerText.includes("nimi") ||
            headerText.includes("name")
        ) && (
            headerText.includes("registr") ||
            headerText.includes("reg") ||
            headerText.includes("email") ||
            headerText.includes("e-post")
        )
    }


    _columnsFromImportHeader(cells: string[]): InvitedCompanyImportColumns {
        const columns: InvitedCompanyImportColumns = {}

        cells.forEach((cell, index) => {
            const lowerCell = cell.toLowerCase()
            if (lowerCell.includes("nimi") || lowerCell.includes("name")) {
                columns.nameIndex = index
            }
            else if (lowerCell.includes("registr") || lowerCell.includes("reg")) {
                columns.registryCodeIndex = index
            }
            else if (lowerCell.includes("email") || lowerCell.includes("e-post")) {
                columns.emailIndex = index
            }
        })

        return columns
    }


    _importedCompanyFromCells(
        cells: string[],
        columns: InvitedCompanyImportColumns | undefined
    ): InvitedCompany | undefined {
        let name = this._cellValueAtIndex(cells, columns?.nameIndex)
        let registryCode = this._cellValueAtIndex(cells, columns?.registryCodeIndex)
        let email = this._cellValueAtIndex(cells, columns?.emailIndex)

        if (!columns) {
            name = cells[0]?.trim() ?? ""
            registryCode = cells[1]?.trim() ?? ""
            email = cells[2]?.trim() ?? ""

            if (cells.length === 2 && cells[1]?.includes("@")) {
                registryCode = ""
                email = cells[1].trim()
            }
        }

        if (!name) {
            return undefined
        }

        if (!registryCode && !email) {
            return undefined
        }

        return {
            name,
            registryCode: registryCode || undefined,
            email: email || undefined,
            isInvited: YES,
            sources: ["manually-added"]
        }
    }


    _cellValueAtIndex(cells: string[], index: number | undefined): string {
        if (index === undefined) {
            return ""
        }
        return cells[index]?.trim() ?? ""
    }


    _mergeImportedCompanies(importedCompanies: InvitedCompany[]): number {
        let changedCount = 0

        for (const importedCompany of importedCompanies) {
            const importedKey = this._identityKeyForImportedCompany(importedCompany)
            const existingCompany = this._companies.find(company =>
                this._identityKeyForImportedCompany(company) === importedKey
            )

            if (existingCompany) {
                existingCompany.isInvited = YES
                if (!existingCompany.sources.includes("manually-added")) {
                    existingCompany.sources = this._sourcesByAddingSourceToCompany(
                        existingCompany,
                        "manually-added"
                    )
                }
                if (importedCompany.email) {
                    existingCompany.email = importedCompany.email
                }
                if (!existingCompany.registryCode && importedCompany.registryCode) {
                    existingCompany.registryCode = importedCompany.registryCode
                }
                const identityKey = this._identityKeyForCompany(existingCompany)
                this._locallyAddedCompanyKeys.add(identityKey)
                this._explicitSelectionOverrideKeys.add(identityKey)
                changedCount++
                continue
            }

            this._companies.push(importedCompany)
            const identityKey = this._identityKeyForCompany(importedCompany)
            this._locallyAddedCompanyKeys.add(identityKey)
            this._explicitSelectionOverrideKeys.add(identityKey)
            changedCount++
        }

        if (changedCount > 0) {
            this._enableManuallyAddedSourcePolicy()
        }

        return changedCount
    }


    _sourcesByAddingSourceToCompany(
        company: Pick<InvitedCompany, "sources">,
        source: RHInvitedCompanySource
    ): RHInvitedCompanySource[] {
        const sources = new Set<RHInvitedCompanySource>(company.sources)
        sources.add(source)
        return INVITED_COMPANY_SOURCE_PRIORITY.filter(candidateSource => sources.has(candidateSource))
    }


    _sourcesByAddingSourcesToCompany(
        company: Pick<InvitedCompany, "sources">,
        incomingSources: RHInvitedCompanySource[]
    ): RHInvitedCompanySource[] {
        const sources = new Set<RHInvitedCompanySource>(company.sources)
        for (const source of incomingSources) {
            sources.add(source)
        }
        return INVITED_COMPANY_SOURCE_PRIORITY.filter(candidateSource => sources.has(candidateSource))
    }


    _identityKeyForImportedCompany(company: Pick<InvitedCompany, "name" | "registryCode" | "email">): string {
        const registryCode = (company.registryCode ?? "").trim()
        if (registryCode) {
            return "registry:" + registryCode
        }

        const email = (company.email ?? "").trim().toLowerCase()
        if (email) {
            return "email:" + email
        }

        return "name:" + company.name.trim().toLowerCase()
    }

    // ─── Descriptors ──────────────────────────────────────────────────────────

    /**
     * Sets up descriptors for the editor context (inside the dialog).
     * Includes the delete button column for manually-added companies.
     */
    setupDescriptors() {
        this._setManagementControlsVisible(YES)
        this.companiesDataView.descriptors = [
            ...this._sharedCompanyDescriptors(),
            CBDataViewCellDescriptorWithObject<InvitedCompany, RHTableActionButtonView>({
                title: "",
                keyPath: "",
                weight: 0,
                absoluteWidth: 48,
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                initCustomView: () => {
                    const buttonView = new RHTableActionButtonView()
                    buttonView.configureWithObject({
                        preferredButtonWidth: RHButtons.dialogCloseSize,
                        preferredButtonHeight: RHButtons.dialogCloseSize
                    })
                    buttonView.button.configureWithObject({
                        hoverText: "Eemalda ettevõte",
                        titleLabel: {
                            text: RHButtons.deleteIconHTML,
                            style: {
                                fontSize: RHButtons.dialogCloseFontSize,
                                lineHeight: RHButtons.dialogCloseLineHeight
                            }
                        },
                        variant: RHButtons.dialogCloseVariant,
                        contentPadding: RHButtons.dialogCloseContentPadding,
                        style: {
                            padding: RHButtons.dialogClosePadding,
                            borderRadius: RHButtons.dialogCloseBorderRadius
                        }
                    })
                    return buttonView
                },
                updateCell: (cellView, dataItem) => {
                    const isDeletable = dataItem.sources.includes("manually-added")
                    cellView.customView.hidden = !isDeletable
                    if (!isDeletable) {
                        return
                    }
                    // noinspection JSConstantReassignment
                    cellView.customView.button.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
                        const shower = CBDialogViewShower.showQuestionDialog(
                            `Kas oled kindel, et soovid ettev\u00f5tte „${RHTextSanitizer.escapedHTML(dataItem.name)}“ nimekirjast eemaldada?`
                        )
                        shower.yesButtonWasPressed = async () => {
                            shower.dialogView.loading = YES
                            this._removeManuallyAddedSourceFromCompany(dataItem)
                            this.companiesDataView.data = this._selectableCompanies()
                            this._seedSelectionStore(this._companies)
                            this._updateRemoveAllManuallyAddedCompaniesButtonState()
                            this._refreshLastInvitationStates()
                            this.companiesDidChange?.()
                            this.manualCompaniesDidChange?.()
                            shower.dialogView.dismiss()
                        }
                    }
                }
            })
        ]

        this._applyCompaniesTableHeaderAppearance()
        this._attachSimilarContractsHeaderTooltip()
        this._bindSharedDescriptorCallbacks()
        this._setupCompanySearchAutocomplete()
        this._setupKeyboardHandlers()
    }


    _removeManuallyAddedSourceFromCompany(company: InvitedCompany) {
        const identityKey = this._identityKeyForCompany(company)
        this._locallyAddedCompanyKeys.delete(identityKey)
        if (company.sources.length <= 1) {
            this._companies.removeElement(company)
            this._explicitSelectionOverrideKeys.delete(identityKey)
            return
        }

        company.sources = company.sources.filter(source => source !== "manually-added")
        if (!this._explicitSelectionOverrideKeys.has(this._identityKeyForCompany(company))) {
            company.isInvited = this._defaultInvitationForCompany(company)
        }
    }


    _requestRemovalOfAllManuallyAddedCompanies() {
        const manuallyAddedCompanies = this._companies.filter(company =>
            company.sources.includes("manually-added")
        )
        if (manuallyAddedCompanies.length === 0) {
            return
        }

        const shower = CBDialogViewShower.showQuestionDialog(
            "Kas oled kindel, et soovid kõik käsitsi lisatud ettevõtted nimekirjast eemaldada?"
        )
        shower.yesButtonWasPressed = async () => {
            shower.dialogView.loading = YES
            for (const company of manuallyAddedCompanies) {
                this._removeManuallyAddedSourceFromCompany(company)
            }
            this.companiesDataView.data = this._selectableCompanies()
            this._seedSelectionStore(this._companies)
            this._updateRemoveAllManuallyAddedCompaniesButtonState()
            this._refreshLastInvitationStates()
            this.companiesDidChange?.()
            this.manualCompaniesDidChange?.()
            shower.dialogView.dismiss()
        }
    }


    _updateRemoveAllManuallyAddedCompaniesButtonState() {
        const hasManuallyAddedCompanies = this._companies.some(company =>
            company.sources.includes("manually-added")
        )
        const shouldShowButton = this._showsManagementControls && hasManuallyAddedCompanies
        this.removeAllManuallyAddedCompaniesButton.configureWithObject({
            hidden: !shouldShowButton,
            enabled: shouldShowButton
        })
    }

    /**
     * Sets up descriptors for the preview context. Replaces the delete button
     * column with a read-only column showing which parts (and their CPV codes)
     * each company was added under.
     *
     * @param partsById  Authoritative map from stable part ID to display data.
     * @param partNumbersByCpvCode  Legacy fallback for snapshots without part IDs.
     *   When provided the column shows "Osa N\n(cpv_code)" per entry.
     *   When absent (e.g. single-part procurements or legacy data) the column
     *   falls back to showing the raw CPV codes.
     */
    setupPreviewDescriptors(
        partsById?: Map<string, RHInvitedCompanyPartDisplayInfo>,
        partNumbersByCpvCode?: Map<string, number[]>
    ) {
        this._setManagementControlsVisible(NO)
        let columnTitle = "CPV-koodid"
        if (partsById && partsById.size > 1) {
            columnTitle = "Osad"
        }

        this.companiesDataView.descriptors = [
            ...this._sharedCompanyDescriptors("preview"),
            CBDataViewCellDescriptorWithObject<InvitedCompany>({
                title: columnTitle,
                keyPath: "",
                // Preview gives its naturally long CPV column the largest flexible share.
                weight: 2.4,
                minimumWidth: 180,
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.left,
                initCell: cellView => {
                    cellView.titleLabel!.configureWithObject({
                        isSingleLine: NO,
                        // Keep each explicitly separated "Osa N (CPV)" entry intact.
                        containerStyle: {
                            whiteSpace: "nowrap",
                            wordBreak: "normal",
                            hyphens: "none"
                        },
                        style: {
                            lineHeight: "1.6",
                            whiteSpace: "nowrap",
                            wordBreak: "normal",
                            hyphens: "none"
                        }
                    })
                },
                updateCell: (cellView, dataItem) => {
                    const partLines: string[] = []
                    for (const partId of dataItem.partIds ?? []) {
                        const part = partsById?.get(partId)
                        if (part) {
                            partLines.push(`Osa ${part.partNumber} (${part.cpvCode})`)
                        }
                    }
                    if (partLines.length > 0) {
                        cellView.titleLabel!.text = partLines.join("<br>")
                        cellView.hoverText = partLines.join("\n")
                        return
                    }

                    const codes = dataItem.cpvCodes ?? []
                    if (codes.length === 0) {
                        cellView.titleLabel!.text = "—"
                        cellView.hoverText = ""
                        return
                    }
                    if (partNumbersByCpvCode && partNumbersByCpvCode.size > 1) {
                        const lines = codes.map(code => {
                            const partNumbers = partNumbersByCpvCode.get(code) ?? []
                            if (partNumbers.length === 0) {
                                return code
                            }
                            if (partNumbers.length === 1) {
                                return `Osa ${partNumbers[0]} (${code})`
                            }
                            return `Osad ${partNumbers.join(", ")} (${code})`
                        })
                        cellView.titleLabel!.text = lines.join("<br>")
                        cellView.hoverText = lines.join("\n")
                    }
                    else {
                        cellView.titleLabel!.text = codes.join("<br>")
                        cellView.hoverText = codes.join("\n")
                    }
                }
            })
        ]

        this._applyCompaniesTableHeaderAppearance(RHInvitedCompaniesViewTokens.previewTableHeaderHeight)
        this._attachSimilarContractsHeaderTooltip()
        this._bindSharedDescriptorCallbacks()
        this._setupCompanySearchAutocomplete()
        this._setupKeyboardHandlers()
    }

    _setManagementControlsVisible(visible: boolean) {
        this._showsManagementControls = visible
        const controls: UIView[] = [
            this.sourceControlsLabel,
            this.statisticsSourceCheckbox,
            this.companyInterestSourceCheckbox,
            this.manuallyAddedSourceCheckbox,
            this.addSectionLabel,
            this.addNameTextField,
            this.addRegcodeTextField,
            this.addEmailTextField,
            this.addCompanyButton,
            this.importSectionLabel,
            this.removeAllManuallyAddedCompaniesButton,
            this.importTextArea,
            this.importCompaniesButton
        ]
        for (const control of controls) {
            control.hidden = !visible
        }
        this._updateRemoveAllManuallyAddedCompaniesButtonState()
        this.setNeedsLayoutUpToRootView()
    }

    private _attachSimilarContractsHeaderTooltip() {
        const headerView = this.companiesDataView.tableHeaderView
        const columnIndex = headerView.descriptors.findIndex(
            descriptor => descriptor.keyPath === "similarContractsCount"
        )
        const headerCell = headerView.cells[columnIndex]
        if (!headerCell) {
            return
        }

        const tooltipTarget = headerCell.buttonView ?? headerCell
        tooltipTarget.viewHTMLElement.setAttribute("aria-label", SIMILAR_CONTRACTS_COLUMN_TOOLTIP)
        UITooltip.sharedInstance.detach(tooltipTarget)
        UITooltip.sharedInstance.attach(tooltipTarget, SIMILAR_CONTRACTS_COLUMN_TOOLTIP)
    }

    _sharedCompanyDescriptors(presentation: "management" | "preview" = "management") {
        const metricColumnWeights = {
            management: {
                taxDebt: 1.6,
                similarContracts: 0.75,
                turnover: 0.9,
                employees: 0.75
            },
            preview: {
                taxDebt: 0,
                similarContracts: 0,
                turnover: 0,
                employees: 0
            }
        }[presentation]
        return [
            {
                type: "selection",
                identifier: this._selectionStoreKey,
                keyPath: "isInvited",
                masterSelectionEnabled: this._showsManagementControls,
                // The fixed selection width preserves space beside Allikas.
                weight: 0,
                absoluteWidth: 80,
                title: "Kutse",
                // Every row reaching this table already passed _companyIsSelectable
                // (see _selectableCompanies) — ineligible rows are filtered out of
                // companiesDataView.data entirely rather than shown disabled.
                enabled: () => this._showsManagementControls,
                // old: "Kutse saaja" — the title lives on the header checkbox's own
                // titleLabel, which cannot wrap, so the long form forced the whole
                // checkbox column wide (see the NB comment in
                // _configureCompaniesTableHeaderCells)
            } as CBSelectionColumnDescriptor<InvitedCompany>,
            CBDataViewCellDescriptorWithObject<InvitedCompany, InvitedCompanySourceBadgesView>({
                title: "Allikas",
                keyPath: "sources",
                allowSorting: YES,
                weight: 0,
                absoluteWidth: 112,
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                initCustomView: () => new InvitedCompanySourceBadgesView(),
                updateCell: (cellView, dataItem: InvitedCompany) => {
                    cellView.customView.updateWithSources(dataItem.sources)
                }
            }),
            CBDataViewCellDescriptorWithObject<InvitedCompany, InvitedCompanyNameView>({
                title: "Ettevõtja",
                keyPath: "name",
                allowSorting: YES,
                // Identity remains the widest flexible column without starving metrics.
                weight: 1.8,
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.left,
                initCustomView: () => new InvitedCompanyNameView(this).configuredWithObject({
                    shouldShowContactDetailsInline: presentation === "preview"
                }),
                updateCell: (cellView, dataItem) => {
                    cellView.customView.updateWithCompany(dataItem)
                }
            }),
            CBDataViewCellDescriptorWithObject<InvitedCompany>({
                title: "Maksuvõlg",
                keyPath: "taxDebt",
                allowSorting: YES,
                // The readable floor keeps long debt badges on one line.
                weight: metricColumnWeights.taxDebt,
                minimumWidth: 160,
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                initCustomView: () => new RHBadgeView(),
                initCell: cellView => {
                    if (!cellView.isMemberOfViewTree) {
                        return
                    }
                    const tooltip = new TaxDebtTooltipView().addedAsSubviewToView(this)
                    this._tooltipMap.set(cellView, tooltip)

                    cellView.customView.addTargetForControlEvent(UIView.controlEvent.PointerHover, () => {
                        const entry = this._debtMap.get(cellView)
                        if (!entry) {
                            return
                        }
                        tooltip.setContent(entry.debt, entry.badgeColor)
                        tooltip.positionedAboveBadgeView(cellView.customView)
                        tooltip.hidden = NO
                    })
                    cellView.customView.addTargetForControlEvent(UIView.controlEvent.PointerLeave, () => {
                        tooltip.hidden = YES
                    })
                },
                updateCell: (cellView, dataItem) => {
                    const { taxDebt } = dataItem
                    const badge = cellView.customView as RHBadgeView

                    if (taxDebt === undefined) {
                        badge.text = "—"
                        badge.setVariant(RHColor.transparentColor, RHColor.textSecondary)
                        this._debtMap.delete(cellView)
                    }
                    else if (taxDebt === null) {
                        badge.text = "Puudub"
                        badge.setVariant(RHColor.success)
                        this._debtMap.delete(cellView)
                    }
                    else {
                        const onSchedule = parseFloat(taxDebt.onPaymentSchedule.replace(",", ".")) > 0
                        const scheduleEndRaw = taxDebt.paymentScheduleEndDate?.trim()
                        const scheduleEndDate = scheduleEndRaw ? new Date(scheduleEndRaw) : null
                        const scheduleExpired = !onSchedule && scheduleEndDate != null && !isNaN(
                            scheduleEndDate.getTime()) && scheduleEndDate < new Date()

                        const badgeColor = onSchedule
                                           ? RHColor.warning
                                           : (scheduleExpired ? RHColor.danger : RHColor.warningOrange)
                        const textColor = onSchedule ? RHColor.textBlack : RHColor.textWhite

                        badge.text = RHEuroFormatter.formattedAmountText(taxDebt.amount)
                        badge.setVariant(badgeColor, textColor)
                        this._debtMap.set(cellView, { debt: taxDebt, badgeColor })
                    }
                }
            }),
            CBDataViewCellDescriptorWithObject<InvitedCompany>({
                title: SIMILAR_CONTRACTS_COLUMN_TITLE,
                keyPath: "similarContractsCount",
                allowSorting: YES,
                weight: metricColumnWeights.similarContracts,
                minimumWidth: 96,
                defaultTitle: "—",
                dataType: "number",
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                updateCell: (cellView, dataItem) => {
                    if (typeof dataItem.similarContractsCount === "number") {
                        cellView.titleLabel!.text =
                            RHEuroFormatter.formattedNumber(dataItem.similarContractsCount)
                    }
                    else {
                        cellView.titleLabel!.text = "—"
                    }
                }
            }),
            {
                title: "Käive (tuh €)",
                keyPath: "taxableTurnoverThousands",
                allowSorting: YES,
                weight: metricColumnWeights.turnover,
                minimumWidth: 110,
                defaultTitle: "—",
                dataType: "number",
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center
            },
            {
                title: "Töötajaid",
                keyPath: "employeesCount",
                allowSorting: YES,
                weight: metricColumnWeights.employees,
                minimumWidth: 92,
                defaultTitle: "—",
                dataType: "number",
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center
            }
        ] satisfies ReturnType<typeof CBDataViewCellDescriptorWithObject>[]
    }

    private _bindSharedDescriptorCallbacks() {
        this.companiesDataView.selectionDidChange = (_identifier) => {
            if (!this._isApplyingSourcePolicy) {
                this._recordSelectionOverridesFromCurrentSelection()
            }
            this.companiesDidChange?.()
        }

        this.companiesDataView.didUpdateTableAfterSearchTextChange = () => {
            this.companiesDataView.tableView._clearKeyboardFocus()
        }

        this.companiesDataView.searchTextField.placeholderText = "Filtreeri tabelit"
    }

    // ─── Search autocomplete ──────────────────────────────────────────────────

    private _applyCompaniesTableHeaderAppearance(
        tableHeaderHeight: number = RHInvitedCompaniesViewTokens.dialogTableHeaderHeight
    ) {
        // Column widths come from the descriptors themselves (weight/absoluteWidth):
        // CBDataView derives and applies them, so only header appearance is set here.
        this.companiesDataView.tableHeaderView.configureWithObject({
            intrinsicContentHeight: () => tableHeaderHeight
        })
        this._configureCompaniesTableHeaderCells()
        this.companiesDataView.tableHeaderView.setNeedsLayout()
        this.companiesDataView.tableView.setNeedsLayout()
    }

    private _configureCompaniesTableHeaderCells() {
        for (const headerCell of this.companiesDataView.tableHeaderView.cells) {
            const buttonView = headerCell.buttonView
            if (buttonView) {
                buttonView.contentPadding = RHInvitedCompaniesViewTokens.multilineHeaderContentPadding
            }

            const titleLabel = buttonView?.titleLabel ?? headerCell.titleLabel
            if (!titleLabel) {
                continue
            }

            titleLabel.isSingleLine = NO
            titleLabel.style.whiteSpace = "normal"
            titleLabel.style.lineHeight = RHInvitedCompaniesViewTokens.multilineHeaderLineHeight
            titleLabel.textElementView.style.whiteSpace = "normal"
            titleLabel.textElementView.style.lineHeight = RHInvitedCompaniesViewTokens.multilineHeaderLineHeight
        }
        // NB: the selection column's header title is carried by the header checkbox's
        // own titleLabel (see CBDataView._wireHeaderCheckboxes), which is laid out as
        // a single centred line; it cannot wrap. The title is therefore kept compact
        // ("Kutse") so the checkbox column can sit at its automatic minimum width.
    }

    private _setupCompanySearchAutocomplete() {
        this.addNameTextField.configureWithObject({
            minCharactersForAutocomplete: 3,
            hideNativeAutocompleteOnExactMatch: NO
        })
        this.addNameTextField.addTargetForControlEvent(
            UITextField.controlEvent.TextChange,
            () => this._onSearchFieldChanged("name", this.addNameTextField.text)
        )

        this.addRegcodeTextField.configureWithObject({
            minCharactersForAutocomplete: 3,
            hideNativeAutocompleteOnExactMatch: NO
        })
        this.addRegcodeTextField.addTargetForControlEvent(
            UITextField.controlEvent.TextChange,
            () => this._onSearchFieldChanged("regcode", this.addRegcodeTextField.text)
        )
    }

    private _onSearchFieldChanged(source: "name" | "regcode", query: string) {
        const trimmed = query.trim()
        const searchGeneration = ++this._companySearchGeneration
        this._tryPopulateFromSelection(source, trimmed)

        if (this._searchDebounceTimer) {
            clearTimeout(this._searchDebounceTimer)
            this._searchDebounceTimer = undefined
        }

        if (trimmed.length < 3) {
            this._lastSearchResults = []
            this.addNameTextField.nativeAutocompleteData = []
            this.addRegcodeTextField.nativeAutocompleteData = []
            return
        }

        this._searchDebounceTimer = setTimeout(() => {
            this._searchDebounceTimer = undefined
            void this._performCompanySearch(source, trimmed, searchGeneration)
        }, 300)
    }

    async _performCompanySearch(
        source: "name" | "regcode",
        query: string,
        searchGeneration: number
    ) {
        try {
            const response = await SocketClient.SearchCompany({ query })
            const result = response.result

            let currentQuery = this.addNameTextField.text.trim()
            if (source === "regcode") {
                currentQuery = this.addRegcodeTextField.text.trim()
            }
            if (
                searchGeneration !== this._companySearchGeneration ||
                query !== currentQuery
            ) {
                return
            }

            if (!result.success || !result.companies) {
                return
            }

            this._lastSearchResults = result.companies

            const formatted = result.companies.map(
                company => `${company.name} (${company.businessRegistryCode})`
            )

            if (source === "name") {
                this.addNameTextField.nativeAutocompleteData = formatted
            }
            else {
                this.addRegcodeTextField.nativeAutocompleteData = formatted
            }
        }
        catch (error) {
            console.error("Company search failed:", error)
        }
    }

    private _tryPopulateFromSelection(source: "name" | "regcode", currentValue: string) {
        if (!this._lastSearchResults.length) {
            return
        }

        const match = this._lastSearchResults.find(
            company => `${company.name} (${company.businessRegistryCode})` === currentValue
        )

        if (!match) {
            return
        }

        this.addNameTextField.text = match.name
        this.addRegcodeTextField.text = match.businessRegistryCode
        this.addEmailTextField.text = match.email ?? ""

        this.addNameTextField.nativeAutocompleteData = []
        this.addRegcodeTextField.nativeAutocompleteData = []
        this._lastSearchResults = []
    }

    // ─── Keyboard handlers ────────────────────────────────────────────────────

    _setupKeyboardHandlers() {
        const searchField = this.companiesDataView.searchTextField
        const tableView = this.companiesDataView.tableView

        const addFields = [this.addNameTextField, this.addRegcodeTextField, this.addEmailTextField]
        for (const addField of addFields) {
            // noinspection JSConstantReassignment
            addField.textElementView.controlEventTargetAccumulator.EnterDown = (_sender, event) => {
                const keyboardEvent = event as KeyboardEvent
                if (keyboardEvent.metaKey || keyboardEvent.ctrlKey) {
                    event.preventDefault()
                    this._addCompanyManually()
                }
            }
        }

        let _lastArrowEventTimestamp = -1

        // noinspection JSConstantReassignment
        searchField.textElementView.controlEventTargetAccumulator.DownArrowDown = (_sender, event) => {
            event.preventDefault()
            const timestamp = (event as KeyboardEvent).timeStamp
            if (timestamp === _lastArrowEventTimestamp) {
                return
            }
            _lastArrowEventTimestamp = timestamp
            const rowCount = this.companiesDataView.filteredData.length
            if (!rowCount) {
                return
            }
            const current = tableView._keyboardFocusedRowIndex ?? -1
            const next = Math.min(current + 1, rowCount - 1)
            if (next !== current) {
                tableView._setKeyboardFocus(next, 0)
            }
        }

        // noinspection JSConstantReassignment
        searchField.textElementView.controlEventTargetAccumulator.UpArrowDown = (_sender, event) => {
            event.preventDefault()
            const timestamp = (event as KeyboardEvent).timeStamp
            if (timestamp === _lastArrowEventTimestamp) {
                return
            }
            _lastArrowEventTimestamp = timestamp
            const rowCount = this.companiesDataView.filteredData.length
            if (!rowCount) {
                return
            }
            const current = tableView._keyboardFocusedRowIndex ?? 0
            const next = Math.max(current - 1, 0)
            if (next !== current) {
                tableView._setKeyboardFocus(next, 0)
            }
        }

        // noinspection JSConstantReassignment
        searchField.textElementView.controlEventTargetAccumulator.EnterDown = async (_sender, event) => {
            event.preventDefault()
            if (!this._showsManagementControls) {
                return
            }
            const isSaveAndClose = (event as KeyboardEvent).metaKey || (event as KeyboardEvent).ctrlKey
            if (isSaveAndClose) {
                this.companiesDidChange?.()
                return
            }
            const rowIndex = tableView._keyboardFocusedRowIndex
            if (rowIndex === undefined) {
                return
            }
            const filteredData = this.companiesDataView.filteredData
            const dataItem = filteredData[rowIndex]
            if (!dataItem) {
                return
            }
            const row = tableView.visibleRowWithIndex(rowIndex) as CBTableRowView<InvitedCompany> | undefined
            if (!row) {
                return
            }
            const selectionCell = row.dataCells[0]
            if (!selectionCell) {
                return
            }
            const checkbox = selectionCell.customView as CBCheckbox
            if (!checkbox) {
                return
            }
            checkbox.selected = !checkbox.selected
            checkbox.sendControlEventForKey("SelectionChange", nil)
        }
    }

    // ─── Layout ───────────────────────────────────────────────────────────────

    override layoutSubviews() {
        super.layoutSubviews()

        const padding = this.core.paddingLength
        const labelHeight = padding * 2
        const bounds = this.contentBoundsWithInset(0)

        if (!this._showsManagementControls) {
            bounds
                .rectangleWithHeight(this.companiesDataView)
                .assignedAsFrameOfView(this.companiesDataView)
            return
        }

        const sourceControlsRowFrame = bounds
            .rectangleWithHeight(labelHeight * 0.85)
            .assignedAsFrameOfView(this.sourceControlsLabel)
            .rectangleForNextRow(
                padding * 0.25,
                Math.max(labelHeight, RHButtons.compactHeight)
            )
        const removeAllButtonWidth =
            this.removeAllManuallyAddedCompaniesButton.titleLabel.intrinsicContentWidth(
                RHButtons.compactHeight
            ) + RHButtons.compactContentPadding * 2

        // Pack the three source toggles at the left, each only as wide as its own
        // control + label, with an even gap between them — instead of stretching
        // them across the full row width (which left large gaps between labels).
        sourceControlsRowFrame
            .rectangleWithInsets(0, removeAllButtonWidth + padding, 0, 0)
            .framesByDistributingViewsAsRow(
                [
                    this.statisticsSourceCheckbox,
                    this.companyInterestSourceCheckbox,
                    this.manuallyAddedSourceCheckbox
                ],
                padding * 1.5,
                [
                    this._sourceToggleWidth(this.statisticsSourceCheckbox, labelHeight),
                    this._sourceToggleWidth(this.companyInterestSourceCheckbox, labelHeight),
                    this._sourceToggleWidth(this.manuallyAddedSourceCheckbox, labelHeight)
                ]
            )
        sourceControlsRowFrame
            .rectangleWithWidth(removeAllButtonWidth, 1)
            .rectangleWithHeight(RHButtons.compactHeight, 0.5)
            .assignedAsFrameOfView(this.removeAllManuallyAddedCompaniesButton)

        const importRowFrame = sourceControlsRowFrame
            .rectangleForNextRow(padding * 0.75, RHInvitedCompaniesViewTokens.companiesTableHeight)
            .assignedAsFrameOfView(this.companiesDataView)
            .rectangleForNextRow(padding * 0.75, labelHeight * 0.85)
            .assignedAsFrameOfView(this.addSectionLabel)
            .rectangleForNextRow(padding * 0.25, labelHeight)
            .distributeViewsAlongWidth(
                [this.addNameTextField, this.addRegcodeTextField, this.addEmailTextField, this.addCompanyButton],
                [3, 1.5, 2, 1],
                padding
            )
            .rectangleForNextRow(
                padding * 0.75,
                labelHeight * 0.85
            )
            .assignedAsFrameOfView(this.importSectionLabel)
            .rectangleForNextRow(padding * 0.25, labelHeight * 3)

        importRowFrame
            .distributeViewsAlongWidth(
                [this.importTextArea, this.importCompaniesButton],
                [3, 1],
                padding,
                [nil, RHInvitedCompaniesViewTokens.manualImportButtonWidth]
            )
        this.importCompaniesButton.frame = this.importCompaniesButton.frame.rectangleWithHeight(
            RHInvitedCompaniesViewTokens.manualImportButtonHeight,
            0.5
        )
    }


    override intrinsicContentHeight(constrainingWidth = 0) {
        if (!this._showsManagementControls) {
            return this.companiesDataView.intrinsicContentHeight(constrainingWidth)
        }
        return super.intrinsicContentHeight(constrainingWidth)
    }


    /**
     * Width just wide enough for one source toggle: the control (leading inset +
     * box + gap to the label) plus the plain-text label's own width, with a small
     * trailing breathing space. Keeps the three toggles tight to their content so
     * they pack to the left rather than stretching across the row.
     */
    _sourceToggleWidth(sourceCheckbox: RHCheckbox, height: number): number {
        const controlWidth = RHForms.checkboxControlLeadingInset +
            RHForms.checkboxControlSize +
            RHForms.checkboxControlGap
        return controlWidth +
            sourceCheckbox.titleLabel.intrinsicContentWidth(height) +
            this.core.paddingLength * 0.5
    }

}
